import React, { useEffect, useState } from "react";
import { Loader2, Save, X, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Section, Btn, Input, Textarea, Field, cardCls } from "@/components/admin/ui";
import MfaCard from "@/components/admin/MfaCard";
import Modal from "@/components/admin/Modal";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function Settings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);

  const set = (k) => (e) => setS((v) => ({ ...v, [k]: e.target.value }));
  const setValue = (k, val) => setS((v) => ({ ...v, [k]: val }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", s); toast.success("Website content updated"); }
    catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    try { await api.post("/admin/send-weekly-report"); toast.success("Summary sent to all recipients"); }
    catch { toast.error("Could not send summary"); }
  };

  const [previewHtml, setPreviewHtml] = useState(null);
  const openPreview = async () => {
    try { const { data } = await api.get("/admin/weekly-report/preview"); setPreviewHtml(data.html); }
    catch { toast.error("Could not load preview"); }
  };
  const sendMonthly = async () => {
    try { await api.post("/admin/send-monthly-report"); toast.success("Monthly summary sent"); }
    catch { toast.error("Could not send monthly summary"); }
  };
  const openMonthlyPreview = async () => {
    try { const { data } = await api.get("/admin/monthly-report/preview"); setPreviewHtml(data.html); }
    catch { toast.error("Could not load preview"); }
  };

  if (!s) return <div className="text-bd-ivory/40">Loading…</div>;

  const values = s.values || [];
  const addValue = () => setValue("values", [...values, ""]);
  const updValue = (i, val) => { const c = [...values]; c[i] = val; setValue("values", c); };
  const delValue = (i) => setValue("values", values.filter((_, idx) => idx !== i));

  const recipients = s.report_recipients || [];
  const addRcpt = () => setValue("report_recipients", [...recipients, ""]);
  const updRcpt = (i, val) => { const c = [...recipients]; c[i] = val; setValue("report_recipients", c); };
  const delRcpt = (i) => setValue("report_recipients", recipients.filter((_, idx) => idx !== i));

  return (
    <div data-testid="admin-settings">
      <Section title="Website Content & Settings" subtitle="Edit public website text, contact details and notification email — no developer needed." action={<Btn data-testid="settings-save" onClick={save} disabled={busy}>{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes</Btn>}>
        <div className="space-y-6 max-w-3xl">
          <div className={`${cardCls} p-6 space-y-5`}>
            <h3 className="font-serif text-xl text-bd-ivory">Homepage hero</h3>
            <Field label="Hero heading"><Input data-testid="set-hero-heading" value={s.hero_heading || ""} onChange={set("hero_heading")} /></Field>
            <Field label="Hero supporting text"><Textarea rows={3} value={s.hero_sub || ""} onChange={set("hero_sub")} /></Field>
            <Field label="Group intro"><Textarea rows={3} value={s.group_intro || ""} onChange={set("group_intro")} /></Field>
            <Field label="Services intro"><Textarea rows={2} value={s.services_intro || ""} onChange={set("services_intro")} /></Field>
          </div>

          <div className={`${cardCls} p-6 space-y-5`}>
            <h3 className="font-serif text-xl text-bd-ivory">About</h3>
            <Field label="Founder statement"><Textarea rows={4} value={s.about_founder || ""} onChange={set("about_founder")} /></Field>
            <Field label="Legacy statement"><Textarea rows={4} value={s.about_legacy || ""} onChange={set("about_legacy")} /></Field>
            <div>
              <label className="block text-xs tracking-[0.12em] uppercase text-bd-ivory/40 mb-2">Group values</label>
              <div className="space-y-2">
                {values.map((v, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={v} onChange={(e) => updValue(i, e.target.value)} />
                    <button onClick={() => delValue(i)} className="text-bd-ivory/40 hover:text-red-400 px-2"><X size={16} /></button>
                  </div>
                ))}
              </div>
              <button onClick={addValue} className="mt-3 text-xs uppercase tracking-wider text-bd-gold hover:text-bd-gold2">+ Add value</button>
            </div>
          </div>

          <div className={`${cardCls} p-6 space-y-5`}>
            <h3 className="font-serif text-xl text-bd-ivory">Contact & company</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Public contact email"><Input data-testid="set-contact-email" value={s.contact_email || ""} onChange={set("contact_email")} /></Field>
              <Field label="Enquiry notification email"><Input data-testid="set-notification-email" value={s.notification_email || ""} onChange={set("notification_email")} /></Field>
              <Field label="Phone"><Input value={s.phone || ""} onChange={set("phone")} /></Field>
              <Field label="Website domain"><Input value={s.domain || ""} onChange={set("domain")} /></Field>
              <Field label="Company name"><Input value={s.company_name || ""} onChange={set("company_name")} /></Field>
              <Field label="Company number"><Input value={s.company_no || ""} onChange={set("company_no")} /></Field>
            </div>
            <Field label="Registered office (leave blank until confirmed)"><Input data-testid="set-registered-office" value={s.registered_office || ""} onChange={set("registered_office")} placeholder="Not yet published" /></Field>
            <p className="text-xs text-bd-ivory/30">The enquiry notification email receives all website submissions. Change it here at any time (e.g. to info@blackdiamondventuresgroupltd.co.uk).</p>
          </div>

          <div className={`${cardCls} p-6 space-y-4`}>
            <div>
              <h3 className="font-serif text-xl text-bd-ivory">Weekly report recipients</h3>
              <p className="text-xs text-bd-ivory/40 mt-1">The Monday summary always goes to the notification email above. Add extra people to receive it too.</p>
            </div>
            <div className="space-y-2">
              {recipients.map((r, i) => (
                <div key={i} className="flex gap-2">
                  <Input data-testid={`report-recipient-${i}`} type="email" value={r} onChange={(e) => updRcpt(i, e.target.value)} placeholder="name@example.com" />
                  <button onClick={() => delRcpt(i)} className="text-bd-ivory/40 hover:text-red-400 px-2"><X size={16} /></button>
                </div>
              ))}
              {recipients.length === 0 && <p className="text-sm text-bd-ivory/30">No extra recipients.</p>}
            </div>
            <button data-testid="report-recipient-add" onClick={addRcpt} className="text-xs uppercase tracking-wider text-bd-gold hover:text-bd-gold2">+ Add recipient</button>

            <div className="border-t border-white/10 pt-5 mt-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Send day (UTC)">
                <select className="w-full bg-bd-black border border-white/15 focus:border-bd-gold outline-none px-4 py-2.5 text-sm text-bd-ivory" data-testid="digest-day" value={s.report_day ?? 0} onChange={(e) => setValue("report_day", Number(e.target.value))}>
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </Field>
              <Field label="Send time (UTC hour)">
                <select className="w-full bg-bd-black border border-white/15 focus:border-bd-gold outline-none px-4 py-2.5 text-sm text-bd-ivory" data-testid="digest-hour" value={s.report_hour ?? 8} onChange={(e) => setValue("report_hour", Number(e.target.value))}>
                  {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                </select>
              </Field>
            </div>
            <div className="flex items-center gap-3 pt-1 flex-wrap">
              <Btn variant="outline" data-testid="digest-preview" onClick={openPreview}><Send size={14} /> Preview summary</Btn>
              <Btn variant="outline" data-testid="digest-send-now" onClick={sendNow}><Send size={14} /> Send test summary now</Btn>
              <span className="text-xs text-bd-ivory/30">Remember to Save changes to apply the schedule.</span>
            </div>

            <div className="border-t border-white/10 pt-5 mt-2">
              <h4 className="font-serif text-lg text-bd-ivory mb-1">Monthly finance summary</h4>
              <p className="text-xs text-bd-ivory/40 mb-4">A separate monthly email with revenue (Stripe when connected), expenses and profit by category.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Send day of month">
                  <select className="w-full bg-bd-black border border-white/15 focus:border-bd-gold outline-none px-4 py-2.5 text-sm text-bd-ivory" data-testid="monthly-day" value={s.monthly_day ?? 1} onChange={(e) => setValue("monthly_day", Number(e.target.value))}>
                    {Array.from({ length: 28 }).map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                  </select>
                </Field>
                <Field label="Send time (UTC hour)">
                  <select className="w-full bg-bd-black border border-white/15 focus:border-bd-gold outline-none px-4 py-2.5 text-sm text-bd-ivory" data-testid="monthly-hour" value={s.monthly_hour ?? 8} onChange={(e) => setValue("monthly_hour", Number(e.target.value))}>
                    {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                  </select>
                </Field>
              </div>
              <div className="flex items-center gap-3 pt-4 flex-wrap">
                <Btn variant="outline" data-testid="monthly-preview" onClick={openMonthlyPreview}><Send size={14} /> Preview monthly</Btn>
                <Btn variant="outline" data-testid="monthly-send-now" onClick={sendMonthly}><Send size={14} /> Send test monthly now</Btn>
              </div>
            </div>
          </div>

          <MfaCard />
        </div>
      </Section>

      <Modal open={!!previewHtml} onClose={() => setPreviewHtml(null)} title="Weekly summary preview" wide testId="digest-preview-modal">
        <p className="text-sm text-bd-ivory/50 mb-4">This is exactly what recipients will receive by email.</p>
        <div className="bg-white">
          <iframe title="Weekly summary preview" srcDoc={previewHtml || ""} className="w-full h-[520px] border border-white/10" />
        </div>
      </Modal>
    </div>
  );
}
